import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Stack,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Mic as MicIcon,
  Stop as StopIcon,
  AutoAwesome as AiIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import SchemaPreview from "./SchemaPreview";

const ConfigModal = ({
  open,
  handleClose,
  selectedType,
  transcript,
  setTranscript,
  isRecording,
  startRecording,
  stopRecording,
  isGenerating,
  handleGenerate,
  generatedSchema,
  setGeneratedSchema,
  onSave,
  isSaving,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AiIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="#1A1A2E"
              fontSize="1.1rem"
            >
              Configure {selectedType?.name || "Form"}
            </Typography>
            <Typography variant="caption" color="#6B7280" fontSize="0.75rem">
              {generatedSchema
                ? "Review the generated form and save"
                : "Describe the condition or record via voice"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#9CA3AF",
            "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ opacity: 0.4, flexShrink: 0 }} />

      <DialogContent
        dividers
        sx={{
          p: 3,
          bgcolor: "#FAFBFC",
          overflow: "auto",
          flex: 1,
          minHeight: 0,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": {
            bgcolor: "#F1F5F9",
            borderRadius: 10,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#CBD5E1",
            borderRadius: 10,
            "&:hover": { bgcolor: "#94A3B8" },
          },
        }}
      >
        {!generatedSchema ? (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="#475467"
                sx={{ mb: 1, display: "block" }}
              >
                CONDITION DESCRIPTION
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Describe the client's condition..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#FFFFFF",
                  },
                }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <IconButton
                onClick={isRecording ? stopRecording : startRecording}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: isRecording
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                  color: "white",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    bgcolor: isRecording
                      ? theme.palette.error.dark
                      : theme.palette.primary.dark,
                    transform: "scale(1.05)",
                  },
                }}
              >
                {isRecording ? <StopIcon /> : <MicIcon />}
              </IconButton>

              <Button
                variant="contained"
                size="large"
                startIcon={
                  isGenerating ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AiIcon />
                  )
                }
                onClick={handleGenerate}
                disabled={isGenerating || !transcript}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  bgcolor: "#2469F0",
                  flexGrow: 1,
                  boxShadow: `0px 4px 12px ${alpha("#2469F0", 0.3)}`,
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: `0px 6px 16px ${alpha("#2469F0", 0.4)}`,
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#E2E8F0",
                    color: "#94A3B8",
                  },
                }}
              >
                {isGenerating ? "AI is building..." : "Generate AI Schema"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert
              severity="warning"
              sx={{
                borderRadius: "10px",
                border: "1px solid #FEDF89",
                bgcolor: "#FFFAEB",
                "& .MuiAlert-icon": { color: "#DC6803" },
              }}
            >
              Review AI Generated Form. Saving will apply this to the client
              profile.
            </Alert>

            <Box
              sx={{
                maxHeight: "55vh",
                overflowY: "auto",
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
              }}
            >
              <SchemaPreview sections={generatedSchema.sections} />
            </Box>

            <Stack direction="row" spacing={2} pt={1}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => setGeneratedSchema(null)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "6px",
                  borderColor: "#D0D5DD",
                  color: "#344054",
                  px: 3,
                  py: 0.75,
                  flexGrow: 1,
                  "&:hover": {
                    bgcolor: "#F9FAFB",
                    borderColor: "#98A2B3",
                  },
                }}
              >
                Redo
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={
                  isSaving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={onSave}
                disabled={isSaving}
                sx={{
                  flexGrow: 2,
                  py: 1.25,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  bgcolor: "#039855",
                  boxShadow: `0px 4px 12px ${alpha("#039855", 0.3)}`,
                  "&:hover": {
                    bgcolor: "#027A48",
                    boxShadow: `0px 6px 16px ${alpha("#039855", 0.4)}`,
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#E2E8F0",
                    color: "#94A3B8",
                  },
                }}
              >
                {isSaving ? "Saving..." : "Save & Apply"}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfigModal;